import React, { createContext, useContext, useEffect, useState } from "react";
import { Modal, Form, InputNumber, message } from "antd";
import { useAuth } from "./auth.context";
import { api } from "../api/api";

interface ExchangeRateContextType {
  exchangeRate: number | null;
  setExchangeRate: (rate: number) => void;
  showExchangeRateModal: () => void;
}

const ExchangeRateContext = createContext<ExchangeRateContextType | undefined>(
  undefined
);

export const ExchangeRateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [exchangeRate, setExchangeRateState] = useState<number | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const { authState } = useAuth();

  useEffect(() => {
    const fetchLastExchangeRate = async () => {
      if (authState.authenticated && (authState.user?.role === "admin")) {
        try {
          const response = await api.get("/settings/last-exchange-rate");
          if (response.data?.exchangeRate) {
            setExchangeRateState(response.data.exchangeRate);
            localStorage.setItem("exchangeRate", response.data.exchangeRate.toString());
          }
        } catch (error) {
          console.error("Error fetching last exchange rate:", error);
        }
      }
    };

    if (authState.authenticated) {
      if (authState.user?.role === "admin") {
        fetchLastExchangeRate();
      } else {
        const storedRate = localStorage.getItem("exchangeRate");
        if (storedRate) {
          setExchangeRateState(parseFloat(storedRate));
        } else {
          setIsModalVisible(true);
        }
      }
    }
  }, [authState.authenticated, authState.user?.role]);

  const setExchangeRate = (rate: number) => {
    setExchangeRateState(rate);
    localStorage.setItem("exchangeRate", rate.toString());
  };

  const showExchangeRateModal = () => {
    if (authState.authenticated) {
      setIsModalVisible(true);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setExchangeRate(values.rate);
      setIsModalVisible(false);
      if (authState.user?.role === "admin") {
        message.success("Курс обмена успешно обновлен");
      }
    } catch (error) {
      message.error("Пожалуйста, введите корректный курс обмена");
    }
  };

  const handleModalCancel = () => {
    if (exchangeRate !== null) {
      setIsModalVisible(false);
      form.resetFields();
    } else {
      message.warning("Необходимо установить курс обмена");
    }
  };

  return (
    <ExchangeRateContext.Provider
      value={{ exchangeRate, setExchangeRate, showExchangeRateModal }}
    >
      {children}
      <Modal
        title="Установить курс обмена"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        closable={exchangeRate !== null}
        maskClosable={exchangeRate !== null}
        keyboard={exchangeRate !== null}
        okText="Установить курс"
        cancelText={exchangeRate === null ? "Отмена" : "Закрыть"}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="rate"
            label="Курс обмена (TJS к USD)"
            rules={[
              { required: true, message: "Пожалуйста, введите курс обмена" },
              { type: "number", min: 0, message: "Курс должен быть больше 0" },
            ]}
            initialValue={exchangeRate}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Введите курс обмена"
              precision={2}
            />
          </Form.Item>
        </Form>
      </Modal>
    </ExchangeRateContext.Provider>
  );
};

export const useExchangeRate = () => {
  const context = useContext(ExchangeRateContext);
  if (context === undefined) {
    throw new Error("useExchangeRate must be used within an ExchangeRateProvider");
  }
  return context;
};
